// ./RegisterComponents/builder-register.ts
import { Builder } from '@builder.io/react';
import { SELUnitDropdown } from '../components/BuilderIO/dropdown';
import { HighlightedDropdown } from '../components/BuilderIO/dropdownHighlight';
import { CaseStudyCardGrid } from '../components/BuilderIO/cardMenuGrid2';
import { ArticleCardGrid } from '../components/BuilderIO/cardMenuGrid3';
import TypeformEmbed from '../components/BuilderIO/typeformIntegration';
import WebinarGridComponent from '../components/BuilderIO/webinarMenuGrid';
import { CalendlyComponent } from '../components/BuilderIO/calendlyPopUp';
import WebinarThumbnailOnly from '../components/BuilderIO/videoPopUp';
import TestimonialsSegment from '../components/BuilderIO/verticalScroll';
import PrimaryButton from '../components/BuilderIO/buttonHover';
import SignUpForm from '../components/BuilderIO/trialSegment';
import FAQGrid from '../components/BuilderIO/faqGrid';

Builder.registerComponent(SELUnitDropdown, {
  name: 'SELUnitDropdown',
  inputs: [
    {
      name: 'units',
      type: 'list',
      subFields: [
        { name: 'title', type: 'text', required: true },
        { name: 'description', type: 'longText' },
      ],
    },
    { name: 'fontFamily', type: 'text', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'fontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 24 },
        { name: 'tablet', type: 'number', defaultValue: 24 },
        { name: 'mobile', type: 'number', defaultValue: 24 },
      ],
      defaultValue: { desktop: 24, tablet: 24, mobile: 24 },
    },
    { name: 'width', type: 'text', defaultValue: '100%' },
    { name: 'height', type: 'text', defaultValue: 'auto' },

    { name: 'titleFontFamily', type: 'text' },
    {
      name: 'titleFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number' },
        { name: 'tablet', type: 'number' },
        { name: 'mobile', type: 'number' },
      ],
    },
    {
      name: 'descriptionFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number' },
        { name: 'tablet', type: 'number' },
        { name: 'mobile', type: 'number' },
      ],
    },

    { name: 'titleFontWeight', type: 'text', defaultValue: 'bold' },
    { name: 'descriptionFontWeight', type: 'text', defaultValue: 'normal' },
    {
      name: 'titleFontStyle',
      type: 'enum',
      enum: ['normal', 'italic'],
      defaultValue: 'normal',
    },
    {
      name: 'descriptionFontStyle',
      type: 'enum',
      enum: ['normal', 'italic'],
      defaultValue: 'normal',
    },
    { name: 'titleColor', type: 'color', defaultValue: '#e65300' },
    { name: 'descriptionColor', type: 'color', defaultValue: 'black' },
  ],
});

Builder.registerComponent(HighlightedDropdown, {
  name: 'HighlightedDropdown',
  inputs: [
    {
      name: 'units',
      type: 'list',
      subFields: [
        { name: 'title', type: 'string', required: true },
        { name: 'description', type: 'html' },
        { name: 'imageUrl', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'] },
      ],
    },

    // FONT SETTINGS
    { name: 'fontFamily', type: 'string', defaultValue: 'GT-Walsheim-Pro-Regular' },
    { name: 'fontSize', type: 'number' },
    { name: 'titleFontFamily', type: 'string' },
    { name: 'descriptionFontFamily', type: 'string' },
    { name: 'titleFontSize', type: 'number' },
    { name: 'descriptionFontSize', type: 'number' },
    { name: 'titleFontWeight', type: 'string', defaultValue: 'bold' },
    { name: 'descriptionFontWeight', type: 'string', defaultValue: 'normal' },
    {
      name: 'titleFontStyle',
      type: 'string',
      enum: ['normal', 'italic'],
      defaultValue: 'normal',
    },
    {
      name: 'descriptionFontStyle',
      type: 'string',
      enum: ['normal', 'italic'],
      defaultValue: 'normal',
    },
    { name: 'titleColor', type: 'color', defaultValue: '#e65300' },
    { name: 'descriptionColor', type: 'color', defaultValue: '#444' },

    // RESPONSIVE IMAGE WIDTH
    {
      name: 'imageWidth',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 300 },
        { name: 'tablet', type: 'number', defaultValue: 250 },
        { name: 'mobile', type: 'number', defaultValue: 200 },
      ],
    },

    // RESPONSIVE IMAGE HEIGHT
    {
      name: 'imageHeight',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 300 },
        { name: 'tablet', type: 'number', defaultValue: 250 },
        { name: 'mobile', type: 'number', defaultValue: 200 },
      ],
    },

    // MENU + WIDTH
    {
      name: 'maxMenuWidth',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 400 },
        { name: 'tablet', type: 'number', defaultValue: 350 },
        { name: 'mobile', type: 'number', defaultValue: 300 },
      ],
    },
    {
      name: 'maxWidth',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 1200 },
        { name: 'tablet', type: 'number', defaultValue: 900 },
        { name: 'mobile', type: 'number', defaultValue: 600 },
      ],
    },
  ],
});

Builder.registerComponent(CaseStudyCardGrid, {
  name: 'Case Study Card Grid',
  description: 'A responsive grid of case study cards with filters and optional link per card.',
  inputs: [
    {
      name: 'cards',
      type: 'list',
      subFields: [
        { name: 'title', type: 'string', defaultValue: 'Case study title' },
        { name: 'description', type: 'richText', defaultValue: 'Short description' },
        { name: 'gradeTag', type: 'string', defaultValue: 'K' },
        { name: 'gradeTagColor', type: 'color', defaultValue: '#FF6B00' },
        { name: 'image', type: 'file', allowedFileTypes: ['image'] },
        { name: 'schoolName', type: 'string' },
        { name: 'schoolLogo', type: 'file', allowedFileTypes: ['image'] },
        { name: 'region', type: 'string' },
        { name: 'link', type: 'url', defaultValue: '' },
      ],
      defaultValue: [
        {
          title: 'Example Case Study',
          description: 'An example description',
          gradeTag: 'Grade 1',
          gradeTagColor: '#FF6B00',
          image: '',
          schoolName: 'Demo School',
          schoolLogo: '',
          region: 'North America',
          link: '',
        },
      ],
    },

    // Title Styles
    {
      name: 'titleFontFamily',
      type: 'string',
      defaultValue: 'GT-Walsheim-Pro-Regular',
    },
    {
      name: 'titleFontSize',
      type: 'object',
      defaultValue: { desktop: '2.1rem', tablet: '1.8rem', mobile: '1.5rem' },
      subFields: [
        { name: 'desktop', type: 'text' },
        { name: 'tablet', type: 'text' },
        { name: 'mobile', type: 'text' },
      ],
    },
    { name: 'titleFontWeight', type: 'string', defaultValue: 'bold' },
    { name: 'titleFontStyle', type: 'string', defaultValue: 'normal' },
    { name: 'titleColor', type: 'color', defaultValue: '#000000' },

    // Description Styles
    { name: 'descriptionFontFamily', type: 'string', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'descriptionFontSize',
      type: 'object',
      defaultValue: { desktop: '1.4rem', tablet: '1.2rem', mobile: '1rem' },
      subFields: [
        { name: 'desktop', type: 'text' },
        { name: 'tablet', type: 'text' },
        { name: 'mobile', type: 'text' },
      ],
    },
    { name: 'descriptionFontWeight', type: 'string', defaultValue: 'normal' },
    { name: 'descriptionFontStyle', type: 'string', defaultValue: 'normal' },
    { name: 'descriptionColor', type: 'color', defaultValue: '#333333' },

    // Dropdown / Filters
    { name: 'dropdownFontFamily', type: 'string', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'dropdownFontSize',
      type: 'object',
      defaultValue: { desktop: '1rem', tablet: '0.9rem', mobile: '0.85rem' },
      subFields: [
        { name: 'desktop', type: 'text' },
        { name: 'tablet', type: 'text' },
        { name: 'mobile', type: 'text' },
      ],
    },
    { name: 'dropdownFontWeight', type: 'string', defaultValue: 'normal' },
    { name: 'dropdownFontStyle', type: 'string', defaultValue: 'normal' },
    { name: 'dropdownColor', type: 'color', defaultValue: '#000000' },

    // School Name Styles
    { name: 'schoolNameFontFamily', type: 'string', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'schoolNameFontSize',
      type: 'object',
      defaultValue: { desktop: '1.5rem', tablet: '1.3rem', mobile: '1.1rem' },
      subFields: [
        { name: 'desktop', type: 'text' },
        { name: 'tablet', type: 'text' },
        { name: 'mobile', type: 'text' },
      ],
    },
    { name: 'schoolNameFontWeight', type: 'string', defaultValue: '600' },
    { name: 'schoolNameFontStyle', type: 'string', defaultValue: 'normal' },
    { name: 'schoolNameColor', type: 'color', defaultValue: '#000000' },

    // New Filter Button Options
    {
      name: 'filterButtonSize',
      type: 'object',
      defaultValue: { desktop: '0.4rem 1rem', tablet: '0.35rem 0.9rem', mobile: '0.3rem 0.8rem' },
      subFields: [
        { name: 'desktop', type: 'text' },
        { name: 'tablet', type: 'text' },
        { name: 'mobile', type: 'text' },
      ],
      friendlyName: 'Filter Button Padding (Desktop / Tablet / Mobile)',
    },
    {
      name: 'filterFontSize',
      type: 'object',
      defaultValue: { desktop: '1rem', tablet: '0.9rem', mobile: '0.85rem' },
      subFields: [
        { name: 'desktop', type: 'text' },
        { name: 'tablet', type: 'text' },
        { name: 'mobile', type: 'text' },
      ],
      friendlyName: 'Filter Text Size (Desktop / Tablet / Mobile)',
    },
  ],
});

Builder.registerComponent(ArticleCardGrid, {
  name: 'ArticleCardGrid',
  inputs: [
    {
      name: 'articles',
      type: 'list',
      subFields: [
        { name: 'title', type: 'text' },
        { name: 'readTime', type: 'text' },
        { name: 'description', type: 'longText' },
        { name: 'imageUrl', type: 'file' },
        { name: 'link', type: 'url' },
      ],
    },
    { name: 'itemsPerPage', type: 'number', defaultValue: 9 },
    { name: 'fontFamily', type: 'text', defaultValue: 'GT-Walsheim-Pro-Regular' },

    // Title typography and responsive font size
    { name: 'titleFontFamily', type: 'text', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'titleFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 24 },
        { name: 'tablet', type: 'number', defaultValue: 20 },
        { name: 'mobile', type: 'number', defaultValue: 18 },
      ],
      defaultValue: { desktop: 24, tablet: 20, mobile: 18 },
    },
    { name: 'titleFontWeight', type: 'text', defaultValue: 'bold' },
    { name: 'titleFontStyle', type: 'text', defaultValue: 'normal' },
    { name: 'titleColor', type: 'color', defaultValue: '#000' },

    // Read time typography and responsive font size
    { name: 'readTimeFontFamily', type: 'text', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'readTimeFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 14 },
        { name: 'tablet', type: 'number', defaultValue: 12 },
        { name: 'mobile', type: 'number', defaultValue: 10 },
      ],
      defaultValue: { desktop: 14, tablet: 12, mobile: 10 },
    },
    { name: 'readTimeColor', type: 'color', defaultValue: '#888' },

    // Description typography and responsive font size
    { name: 'descriptionFontFamily', type: 'text', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'descriptionFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 14 },
        { name: 'tablet', type: 'number', defaultValue: 12 },
        { name: 'mobile', type: 'number', defaultValue: 10 },
      ],
      defaultValue: { desktop: 14, tablet: 12, mobile: 10 },
    },
    { name: 'descriptionFontWeight', type: 'text', defaultValue: 'normal' },
    { name: 'descriptionFontStyle', type: 'text', defaultValue: 'normal' },
    { name: 'descriptionColor', type: 'color', defaultValue: '#444' },

    // Link typography and responsive font size
    { name: 'linkFontFamily', type: 'text', defaultValue: 'GT-Walsheim-Pro-Regular' },
    {
      name: 'linkFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 16 },
        { name: 'tablet', type: 'number', defaultValue: 14 },
        { name: 'mobile', type: 'number', defaultValue: 12 },
      ],
      defaultValue: { desktop: 16, tablet: 14, mobile: 12 },
    },
    { name: 'linkFontWeight', type: 'text', defaultValue: 'bold' },
    { name: 'linkFontStyle', type: 'text', defaultValue: 'normal' },
    { name: 'linkColor', type: 'color', defaultValue: '#63adf0' },
  ],
});

// Builder.io registration
Builder.registerComponent(WebinarGridComponent, {
  name: 'WebinarGridComponent',
  inputs: [
    {
      name: 'cards',
      type: 'list',
      subFields: [
        { name: 'thumbnail', type: 'file', allowedFileTypes: ['jpg', 'png', 'webp'] },
        { name: 'videoId', type: 'string' },
        { name: 'videoType', type: 'string', enum: ['youtube', 'mux'] },
        { name: 'title', type: 'string' },
        { name: 'description', type: 'string' },
        { name: 'date', type: 'string' },
      ],
    },
    // Title
    {
      name: 'titleFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number' },
        { name: 'tablet', type: 'number' },
        { name: 'mobile', type: 'number' },
      ],
    },
    {
      name: 'titleColor',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'color' },
        { name: 'tablet', type: 'color' },
        { name: 'mobile', type: 'color' },
      ],
    },
    // Description
    {
      name: 'descriptionFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number' },
        { name: 'tablet', type: 'number' },
        { name: 'mobile', type: 'number' },
      ],
    },
    {
      name: 'descriptionColor',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'color' },
        { name: 'tablet', type: 'color' },
        { name: 'mobile', type: 'color' },
      ],
    },
    // Date
    {
      name: 'dateFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number' },
        { name: 'tablet', type: 'number' },
        { name: 'mobile', type: 'number' },
      ],
    },
    {
      name: 'dateColor',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'color' },
        { name: 'tablet', type: 'color' },
        { name: 'mobile', type: 'color' },
      ],
    },
    {
      name: 'dateBgColor',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'color' },
        { name: 'tablet', type: 'color' },
        { name: 'mobile', type: 'color' },
      ],
    },
    // Load more button
    {
      name: 'loadMoreFontSize',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'number' },
        { name: 'tablet', type: 'number' },
        { name: 'mobile', type: 'number' },
      ],
    },
    {
      name: 'loadMoreTextColor',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'color' },
        { name: 'tablet', type: 'color' },
        { name: 'mobile', type: 'color' },
      ],
    },
    {
      name: 'loadMoreBgColor',
      type: 'object',
      subFields: [
        { name: 'desktop', type: 'color' },
        { name: 'tablet', type: 'color' },
        { name: 'mobile', type: 'color' },
      ],
    },
  ],
});

Builder.registerComponent(CalendlyComponent, {
  name: 'CalendlyComponent',
  inputs: [
    {
      name: 'bookingUrl',
      type: 'string',
      required: true,
      defaultValue: 'https://calendly.com/your-demo-link',
      friendlyName: 'Booking URL',
    },
    {
      name: 'buttonText',
      type: 'object',
      friendlyName: 'Button Text',
      subFields: [
        { name: 'desktop', type: 'string', defaultValue: 'Book a Demo', friendlyName: 'Desktop' },
        { name: 'tablet', type: 'string', defaultValue: 'Book a Demo', friendlyName: 'Tablet' },
        { name: 'mobile', type: 'string', defaultValue: 'Book a Demo', friendlyName: 'Mobile' },
      ],
    },
    {
      name: 'buttonColor',
      type: 'object',
      friendlyName: 'Button Color',
      subFields: [
        { name: 'desktop', type: 'color', defaultValue: '#00aaff', friendlyName: 'Desktop' },
        { name: 'tablet', type: 'color', defaultValue: '#00aaff', friendlyName: 'Tablet' },
        { name: 'mobile', type: 'color', defaultValue: '#00aaff', friendlyName: 'Mobile' },
      ],
    },
    {
      name: 'buttonTextColor',
      type: 'object',
      friendlyName: 'Button Text Color',
      subFields: [
        { name: 'desktop', type: 'color', defaultValue: '#ffffff', friendlyName: 'Desktop' },
        { name: 'tablet', type: 'color', defaultValue: '#ffffff', friendlyName: 'Tablet' },
        { name: 'mobile', type: 'color', defaultValue: '#ffffff', friendlyName: 'Mobile' },
      ],
    },
    {
      name: 'buttonWidth',
      type: 'object',
      friendlyName: 'Button Width',
      subFields: [
        { name: 'desktop', type: 'string', defaultValue: '200px', friendlyName: 'Desktop' },
        { name: 'tablet', type: 'string', defaultValue: '160px', friendlyName: 'Tablet' },
        { name: 'mobile', type: 'string', defaultValue: '140px', friendlyName: 'Mobile' },
      ],
    },
    {
      name: 'buttonHeight',
      type: 'object',
      friendlyName: 'Button Height',
      subFields: [
        { name: 'desktop', type: 'string', defaultValue: '48px', friendlyName: 'Desktop' },
        { name: 'tablet', type: 'string', defaultValue: '40px', friendlyName: 'Tablet' },
        { name: 'mobile', type: 'string', defaultValue: '36px', friendlyName: 'Mobile' },
      ],
    },
    {
      name: 'buttonFontFamily',
      type: 'object',
      friendlyName: 'Button Font Family',
      subFields: [
        { name: 'desktop', type: 'string', defaultValue: 'Arial, sans-serif', friendlyName: 'Desktop' },
        { name: 'tablet', type: 'string', defaultValue: 'Arial, sans-serif', friendlyName: 'Tablet' },
        { name: 'mobile', type: 'string', defaultValue: 'Arial, sans-serif', friendlyName: 'Mobile' },
      ],
    },
    {
      name: 'buttonFontSize',
      type: 'object',
      friendlyName: 'Button Font Size',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 16, friendlyName: 'Desktop' },
        { name: 'tablet', type: 'number', defaultValue: 14, friendlyName: 'Tablet' },
        { name: 'mobile', type: 'number', defaultValue: 14, friendlyName: 'Mobile' },
      ],
    },
  ],
});

Builder.registerComponent(TypeformEmbed, {
  name: 'Typeform Embed',
  inputs: [
    {
      name: 'formUrl',
      type: 'url',
      required: true,
      defaultValue: '',
      helperText: 'URL of the Typeform form to embed',
    },
    {
      name: 'height',
      type: 'number',
      defaultValue: 500,
      helperText: 'Height of the iframe in pixels',
      min: 100,
    },
  ],
});

Builder.registerComponent(WebinarThumbnailOnly, {
  name: 'Video pop up',
  inputs: [
    {
      name: 'thumbnail',
      type: 'file',
      required: true,
      helperText: 'Thumbnail image for the video',
    },
    {
      name: 'videoId',
      type: 'string',
      required: true,
      helperText: 'Video ID (YouTube ID or Mux playback ID)',
    },
    {
      name: 'videoType',
      type: 'string',
      enum: ['youtube', 'mux'],
      required: true,
      helperText: 'Type of the video source',
    },
    {
      name: 'thumbnailSize',
      type: 'number',
      defaultValue: 320,
      min: 100,
      max: 1000,
      helperText: 'Width and height in pixels for the thumbnail',
    },
    {
      name: 'roundedCorners',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable rounded corners on the thumbnail',
    },
  ],
});

Builder.registerComponent(TestimonialsSegment, {
  name: 'Testimonials Segment',
  inputs: [
    { name: 'sectionTitle', type: 'string', defaultValue: 'Learn Why Educators Fall in Love with Our Product' },
    { name: 'backgroundColor', type: 'color', defaultValue: '#f8f9fa' },

    {
      name: 'testimonials',
      type: 'list',
      subFields: [
        { name: 'quotation', type: 'string', required: true },
        { name: 'attribution', type: 'string', required: true },
        { name: 'logoUrl', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'] },
        { name: 'logoAlt', type: 'string' },
      ],
    },

    // Typography
    { name: 'headerFontFamily', type: 'string', defaultValue: 'Georgia, serif' },
    { name: 'headerFontWeight', type: 'string', defaultValue: '700' },
    { name: 'headerFontSizeDesktop', type: 'string', defaultValue: '2.5rem' },
    { name: 'headerFontSizeTablet', type: 'string', defaultValue: '2rem' },
    { name: 'headerFontSizeMobile', type: 'string', defaultValue: '1.75rem' },
    { name: 'headerTextColor', type: 'color', defaultValue: '#333' },

    { name: 'quoteFontFamily', type: 'string', defaultValue: 'Georgia, serif' },
    { name: 'quoteFontWeight', type: 'string', defaultValue: '300' },
    { name: 'quoteFontSizeDesktop', type: 'string', defaultValue: '1.25rem' },
    { name: 'quoteFontSizeTablet', type: 'string', defaultValue: '1.125rem' },
    { name: 'quoteFontSizeMobile', type: 'string', defaultValue: '1rem' },
    { name: 'quoteTextColor', type: 'color', defaultValue: '#555' },

    { name: 'attributionFontFamily', type: 'string', defaultValue: 'Arial, sans-serif' },
    { name: 'attributionFontWeight', type: 'string', defaultValue: '600' },
    { name: 'attributionFontSizeDesktop', type: 'string', defaultValue: '1rem' },
    { name: 'attributionFontSizeTablet', type: 'string', defaultValue: '0.9rem' },
    { name: 'attributionFontSizeMobile', type: 'string', defaultValue: '0.85rem' },
    { name: 'attributionTextColor', type: 'color', defaultValue: '#777' },

    // Logo sizes
    { name: 'logoSizeDesktop', type: 'string', defaultValue: '200px' },
    { name: 'logoSizeTablet', type: 'string', defaultValue: '160px' },
    { name: 'logoSizeMobile', type: 'string', defaultValue: '120px' },

    // Arrows per breakpoint
    { name: 'arrowSizeDesktop', type: 'string', defaultValue: '2rem' },
    { name: 'arrowSizeTablet', type: 'string', defaultValue: '1.75rem' },
    { name: 'arrowSizeMobile', type: 'string', defaultValue: '1.5rem' },

    { name: 'arrowColorDesktop', type: 'color', defaultValue: '#333' },
    { name: 'arrowColorTablet', type: 'color', defaultValue: '#333' },
    { name: 'arrowColorMobile', type: 'color', defaultValue: '#333' },

    { name: 'arrowBackgroundColor', type: 'color', defaultValue: 'rgba(255,255,255,0.9)' },

    { name: 'showActiveLogoOnly', type: 'boolean', defaultValue: false },
  ],
});

Builder.registerComponent(PrimaryButton, {
  name: 'PrimaryButton',
  inputs: [
    { name: 'children', type: 'string', defaultValue: 'Click Me' },

    // Colors
    { name: 'backgroundColor', type: 'color', defaultValue: '#007bff' },
    { name: 'hoverColor', type: 'color', defaultValue: '#0056b3' },
    { name: 'textColor', type: 'color', defaultValue: '#ffffff' },
    {
      name: 'hoverTextColor',
      type: 'object',
      friendlyName: 'Hover Text Color',
      subFields: [
        { name: 'desktop', type: 'color', defaultValue: '#ff0000' },
        { name: 'tablet', type: 'color', defaultValue: '#00ff00' },
        { name: 'mobile', type: 'color', defaultValue: '#0000ff' },
      ],
    },
    { name: 'borderColor', type: 'color' },
    { name: 'hoverBorderColor', type: 'color' },

    // Typography
    { name: 'fontSize', type: 'number', defaultValue: 16, helperText: 'Font size in px', responsive: true },
    { name: 'fontFamily', type: 'string' },

    // Spacing
    { name: 'paddingVertical', type: 'number', defaultValue: 12 },
    { name: 'paddingHorizontal', type: 'number', defaultValue: 24 },
    { name: 'borderRadius', type: 'number', defaultValue: 6 },

    // Dimensions
    { name: 'width', type: 'number', responsive: true },
    { name: 'height', type: 'number', responsive: true },

    // Link
    { name: 'link', type: 'url' },
    { name: 'openInNewTab', type: 'boolean', defaultValue: false },
  ],
});

Builder.registerComponent(SignUpForm, {
  name: 'SignUpForm',
  inputs: [
    { name: 'formDescription', type: 'longText', defaultValue: 'Please fill in your details below to register.' },
    { name: 'trial', type: 'boolean', defaultValue: true },
    { name: 'curriculum', type: 'string', enum: ['elementary', 'middle', 'high'], defaultValue: 'elementary' },
    { name: 'hasFormDescription', type: 'boolean', defaultValue: true },
    { name: 'titleColor', type: 'color', defaultValue: 'white' },
    { name: 'titleBackgroundColor', type: 'color', defaultValue: '#007BFF' },
    { name: 'descriptionColor', type: 'color', defaultValue: '#444' },
    { name: 'buttonBackgroundColor', type: 'color', defaultValue: '#007BFF' },
    { name: 'buttonTextColor', type: 'color', defaultValue: '#fff' },
    { name: 'buttonText', type: 'string', defaultValue: 'Register Now' },
  ],
});

Builder.registerComponent(FAQGrid, {
  name: 'FAQGrid',
  inputs: [
    {
      name: 'faqs',
      type: 'list',
      subFields: [
        { name: 'question', type: 'string', required: true },
        { name: 'answer', type: 'string', required: true },
      ],
      required: true,
      defaultValue: [
        { question: 'Question 1?', answer: 'Answer 1' },
        { question: 'Question 2?', answer: 'Answer 2' },
      ],
    },
    {
      name: 'fontFamily',
      type: 'string',
      defaultValue: 'Inter, sans-serif',
      friendlyName: 'Font Family',
      description: 'CSS font-family for all text',
    },
    {
      name: 'questionFontSize',
      type: 'object',
      defaultValue: { desktop: 20, tablet: 18, mobile: 16 },
      friendlyName: 'Question Font Sizes',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 20 },
        { name: 'tablet', type: 'number', defaultValue: 18 },
        { name: 'mobile', type: 'number', defaultValue: 16 },
      ],
    },
    {
      name: 'answerFontSize',
      type: 'object',
      defaultValue: { desktop: 16, tablet: 15, mobile: 14 },
      friendlyName: 'Answer Font Sizes',
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 16 },
        { name: 'tablet', type: 'number', defaultValue: 15 },
        { name: 'mobile', type: 'number', defaultValue: 14 },
      ],
    },
    {
      name: 'questionFontWeight',
      type: 'number',
      defaultValue: 700,
      friendlyName: 'Question Font Weight',
      description: 'Font weight for the question text',
    },
    {
      name: 'answerFontWeight',
      type: 'number',
      defaultValue: 400,
      friendlyName: 'Answer Font Weight',
      description: 'Font weight for the answer text',
    },
  ],
});
